import React, {
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHandleLogout } from "../hooks/useHandleLogout";
import { RoutePaths } from "../enumStrings";
import { Route, Routes } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  ItemsPurchasableByChamberEnum,
  UserType,
  useRecordCmpStaffDashboardLoginMutation,
} from "../graphql/types";
import useGetChamberFromUserIdInAuth from "../hooks/useGetChamberFromUserIdInAuth";
import Error404 from "./pages/ErrorPages/Error404";
import Error500 from "./pages/ErrorPages/Error500";
import FullPageLoader from "./common/FullPageLoader";
import { useGetLoggedInAppInfo } from "../hooks/useGetLoggedInAppInfo";
import Layout from "./Layout/Layout";
import useScrollToTop from "../hooks/useScrollToTop";
import { UserTypeContext } from "../contexts/UserTypeContext";
import { PRESERVE_ERROR_FOR_CALLER_CONTEXT } from "../utils/apolloErrorPolicy";
import { buildStaffErrorFallback } from "../utils/userFacingError";
import { CmpOrganizationNounProvider } from "../contexts/CmpOrganizationNounContext";
import useIsOffline from "../hooks/useIsOffline";
import OfflineScreen from "./common/offline/OfflineScreen";

const LogoutManual = (): ReactElement => {
  const handleLogout = useHandleLogout();

  useEffect(() => {
    handleLogout();
    // only run on page load that's why ignoring
    // eslint-disable-next-line
  }, []);

  return <></>;
};

const LogoutForDisabledAdmin = (): ReactElement => {
  const handleLogout = useHandleLogout();

  useEffect(() => {
    const runAtPageLoad = async () => {
      const authSession = await fetchAuthSession();

      if (
        authSession &&
        authSession.tokens &&
        authSession.tokens.accessToken &&
        authSession.tokens.accessToken.payload &&
        authSession.tokens.accessToken.payload["custom:isDisabled"] === "true"
      ) {
        handleLogout("accountDisabled");
      }
    };

    runAtPageLoad();

    // only run on page load that's why ignoring
    // eslint-disable-next-line
  }, []);

  return <></>;
};

// const NavigateToSuspensionPageForSuspendedChamberMemberAdmin =
//   (): ReactElement => {
//     const location = useLocation();
//     useEffect(() => {
//       if (location.pathname !== RoutePaths.suspendedChamberMember) {
//         window.location.href = RoutePaths.suspendedChamberMember;
//       }
//     }, [location]);

//     return <></>;
//   };

const LoggedInApp = (): ReactElement => {
  const handleLogout = useHandleLogout();
  const chamberInfo = useGetChamberFromUserIdInAuth();
  const [isLoading, setIsLoading] = useState(true);
  const {
    state: { userType },
  } = useContext(UserTypeContext);
  const [recordStaffLogin] = useRecordCmpStaffDashboardLoginMutation({
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
  });
  const staffLoginRecorded = useRef(false);

  // Scroll to top when switching pages
  useScrollToTop();

  useEffect(() => {
    if (chamberInfo) {
      if (
        !(chamberInfo?.purchasedItems || []).includes(
          ItemsPurchasableByChamberEnum.ChamberMemberPro
        )
      ) {
        handleLogout("noChamberMemberPro");
      } else {
        setIsLoading(false);
      }
    }
  }, [chamberInfo, handleLogout]);

  useEffect(() => {
    if (
      userType !== UserType.ChamberStaff ||
      staffLoginRecorded.current
    ) {
      return;
    }

    staffLoginRecorded.current = true;
    recordStaffLogin().catch(() => {
      // Last-login tracking must not block an otherwise valid dashboard
      // session. A later login will retry.
    });
  }, [recordStaffLogin, userType]);

  const {
    userRoutesToShow,
    userInfoLoading,
    userInfoError,
  } = useGetLoggedInAppInfo();
  const isOffline = useIsOffline();

  // Offline design C: the dashboard could not load its first data with no
  // internet. After it has loaded, the offline banner in Layout shows instead.
  if (isOffline && (userInfoError || isLoading || userInfoLoading)) {
    return <OfflineScreen />;
  }

  // An API failure sends staff to /500, and the same failure can stop the
  // dashboard from loading. Then the 500 card shows without the sidebar,
  // instead of the loader that does not end.
  if (window.location.pathname === RoutePaths.error500 && userInfoError) {
    return <Error500 placement="standalone" />;
  }

  if (userInfoError) {
    return (
      <div className="mx-auto mt-20 max-w-xl rounded-lg border border-red-200 bg-red-50 p-6 text-center text-red-900">
        <p className="font-semibold">
          {buildStaffErrorFallback("load your dashboard")}
        </p>
        <button
          type="button"
          className="mt-4 rounded-md border border-red-300 bg-white px-4 py-2 font-semibold"
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <>
      {isLoading || userInfoLoading ? (
        <FullPageLoader />
      ) : (
        <CmpOrganizationNounProvider chamberName={chamberInfo?.name}>
          <Layout>
            <LogoutForDisabledAdmin />
            <Routes>
              <Route path={RoutePaths.logout} element={<LogoutManual />} />
              <Route path={RoutePaths.error500} element={<Error500 />} />
              {userRoutesToShow}
              <Route path="*" element={<Error404 />} />
            </Routes>
          </Layout>
        </CmpOrganizationNounProvider>
      )}
    </>
  );
};

export default LoggedInApp;
